import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { usePersonalization } from '@/contexts/PersonalizationContext';

interface AnimationContextType {
  enabled: boolean;
  speed: number;
  reducedMotion: boolean;
  pageTransitions: boolean;
  pageTransition3D: 'flip' | 'barrel' | 'cube' | 'none';
  cardReveals: boolean;
  particleEffects: boolean;
  galaxyBackground: boolean;
  toggleAnimations: () => void;
  setSpeed: (speed: number) => void;
}

const AnimationContext = createContext<AnimationContextType | undefined>(undefined);

export function AnimationProvider({ children }: { children: ReactNode }) {
  const { preferences } = usePersonalization();
  const [enabled, setEnabled] = useState(true);
  const [speed, setSpeedState] = useState(1.0);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Read animation preferences from personalization context
  useEffect(() => {
    if (preferences?.animation_preferences) {
      const prefs = preferences.animation_preferences;
      
      // Ensure speed is always a float
      const speed = typeof prefs.speed === 'number' ? prefs.speed : parseFloat(String(prefs.speed)) || 1.0;
      setSpeedState(speed);
      
      // Check if accessibility settings override
      if (preferences?.accessibility_settings?.reducedMotion) {
        setEnabled(false);
        setReducedMotion(true);
      }
    }
  }, [preferences]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    if (mediaQuery.matches) {
      setEnabled(false);
    }

    const handleChange = () => {
      setReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) {
        setEnabled(false);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleAnimations = () => {
    if (!reducedMotion) {
      setEnabled(prev => !prev);
    }
  };

  const setSpeed = (newSpeed: number) => {
    setSpeedState(Math.max(0.5, Math.min(2, newSpeed)));
  };

  // Get individual animation preferences
  const pageTransitions = preferences?.animation_preferences?.pageTransitions !== false;
  const pageTransition3D = (preferences?.animation_preferences?.pageTransition3D as 'flip' | 'barrel' | 'cube' | 'none') || 'flip';
  const cardReveals = preferences?.animation_preferences?.cardReveals !== false;
  const particleEffects = preferences?.animation_preferences?.particleEffects !== false;
  const galaxyBackground = preferences?.animation_preferences?.galaxyBackground !== false;

  return (
    <AnimationContext.Provider 
      value={{ 
        enabled, 
        speed, 
        reducedMotion, 
        pageTransitions,
        pageTransition3D,
        cardReveals,
        particleEffects,
        galaxyBackground,
        toggleAnimations, 
        setSpeed 
      }}
    >
      {children}
    </AnimationContext.Provider>
  );
}

export function useAnimation() {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error('useAnimation must be used within AnimationProvider');
  }
  return context;
}
